import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, Timer, Sparkles } from "lucide-react";
import confetti from "canvas-confetti";
import { BOMB, buildLayout, scoreRound, getBest, bestKey } from "../../game/config";
import { Tile } from "./Tile";
import { Hud } from "./Hud";
import { GameOverDialog } from "./GameOverDialog";
import { SequenceStrip } from "./SequenceStrip";

const ReadyPrompt = ({ onReady }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="font-display text-lg font-bold text-white sm:text-xl">Take your time — memorize!</span>
    <button data-testid="ready-btn" onClick={onReady} className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 px-5 py-2.5 font-display font-black text-white shadow-lg transition-transform hover:scale-105 active:scale-95">
      <Eye className="h-4 w-4" /> I'm ready
    </button>
  </div>
);

const StatusBar = ({ phase, timerOn, countdown, total, next, elapsed, onReady, category }) => (
  <div className="mt-6 min-h-[64px]">
    {phase === "memorize" && !timerOn ? (
      <ReadyPrompt onReady={onReady} />
    ) : phase === "memorize" ? (
      <div>
        <div className="flex items-baseline justify-between text-white">
          <span className="font-display text-lg font-bold sm:text-xl">Memorize!</span>
          <span data-testid="memorize-countdown" className="font-display text-2xl font-black text-cyan-300">{countdown.toFixed(1)}s</span>
        </div>
        <div className="mt-2 h-3 overflow-hidden rounded-full bg-white/10">
          <div className="h-full rounded-full bg-gradient-to-r from-cyan-400 via-fuchsia-500 to-amber-400" style={{ width: `${(countdown / total) * 100}%`, transition: "width 100ms linear" }} />
        </div>
      </div>
    ) : phase === "play" ? (
      <div className="flex items-center justify-between">
        <p data-testid="next-number-hint" className="flex items-center gap-2 font-display text-lg font-bold text-white sm:text-xl">
          Find <span className="grid h-9 min-w-9 place-items-center rounded-lg bg-white/15 px-2 text-amber-300">{category.render(next)}</span>
        </p>
        <span data-testid="solve-timer" className="inline-flex items-center gap-1.5 font-display text-lg font-black text-cyan-300"><Timer className="h-4 w-4" />{elapsed.toFixed(1)}s</span>
      </div>
    ) : null}
  </div>
);

const RoundBanner = ({ round, stats }) => (
  <motion.div data-testid="round-win-banner" initial={{ opacity: 0, scale: 0.6, rotate: -6 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} exit={{ opacity: 0, scale: 0.8 }}
    className="pointer-events-none absolute inset-0 grid place-items-center">
    <div className="rounded-3xl bg-gradient-to-r from-emerald-400 via-cyan-400 to-fuchsia-500 px-8 py-5 text-center font-display shadow-2xl">
      <div className="text-3xl font-black text-white sm:text-4xl">Round {round} cleared!</div>
      <div data-testid="round-points" className="mt-1 text-2xl font-black text-white">+{stats.total} pts</div>
      <div className="mt-1 text-sm text-white/90">
        {stats.base} base · {stats.speed} speed · {stats.hearts} hearts{stats.multiplier > 1 ? ` · ×${stats.multiplier} timer` : ""}
      </div>
      {stats.flawless > 0 && (
        <div data-testid="flawless-bonus" className="mt-2 inline-flex items-center gap-1 rounded-full bg-white/25 px-3 py-1 text-sm font-black text-white">
          <Sparkles className="h-4 w-4" /> Flawless! +{stats.flawless} bonus
        </div>
      )}
    </div>
  </motion.div>
);

export default function GameScreen({ mode, category, timerOn, play, muted, onMute, onExit }) {
  const [layout, setLayout] = useState(() => buildLayout(mode));
  const [statuses, setStatuses] = useState({});
  const [phase, setPhase] = useState("memorize");
  const [round, setRound] = useState(1);
  const [score, setScore] = useState(0);
  const [points, setPoints] = useState(0);
  const [roundStats, setRoundStats] = useState(null);
  const [lives, setLives] = useState(mode.lives);
  const [next, setNext] = useState(1);
  const [countdown, setCountdown] = useState(mode.memorize);
  const [elapsed, setElapsed] = useState(0);
  const [best, setBest] = useState(getBest(mode.id, timerOn));
  const [newBest, setNewBest] = useState(false);
  const [mistakes, setMistakes] = useState(0);
  const [bestFlash, setBestFlash] = useState(false);
  const timers = useRef([]);
  const playStart = useRef(0);
  const celebrated = useRef(false);

  const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const celebrate = useCallback(() => {
    celebrated.current = true;
    setBestFlash(true);
    timers.current.push(setTimeout(() => setBestFlash(false), 2500));
    const colors = ["#22d3ee", "#e879f9", "#fbbf24", "#34d399", "#ff2a6d"];
    confetti({ particleCount: 140, spread: 90, origin: { y: 0.6 }, colors });
    confetti({ particleCount: 60, angle: 60, spread: 60, origin: { x: 0 }, colors });
    confetti({ particleCount: 60, angle: 120, spread: 60, origin: { x: 1 }, colors });
  }, []);

  useEffect(() => {
    if (best > 0 && points > best && !celebrated.current) celebrate();
  }, [points, best, celebrate]);

  const startRound = useCallback((r, prev) => {
    setLayout(buildLayout(mode, prev));
    setStatuses({});
    setNext(1);
    setMistakes(0);
    setRound(r);
    setCountdown(mode.memorize);
    setElapsed(0);
    setPhase("memorize");
  }, [mode]);

  const beginPlay = useCallback(() => {
    playStart.current = Date.now();
    setPhase("play");
    play("flip");
  }, [play]);

  useEffect(() => {
    if (phase !== "memorize" || !timerOn) return;
    const id = setInterval(() => {
      setCountdown((c) => {
        const n = Math.max(0, c - 0.1);
        if (n <= 3 && Math.abs(n - Math.round(n)) < 0.05 && n > 0) play("tick");
        return n;
      });
    }, 100);
    return () => clearInterval(id);
  }, [phase, play, timerOn]);

  useEffect(() => {
    if (phase === "memorize" && timerOn && countdown <= 0) beginPlay();
  }, [countdown, phase, timerOn, beginPlay]);

  useEffect(() => {
    if (phase !== "play") return;
    const id = setInterval(() => setElapsed((Date.now() - playStart.current) / 1000), 100);
    return () => clearInterval(id);
  }, [phase]);

  const endGame = () => {
    setPhase("over");
    play("gameover");
    if (points > best) {
      localStorage.setItem(bestKey(mode.id, timerOn), String(points));
      setBest(points);
      setNewBest(true);
      if (!celebrated.current) celebrate();
    }
  };

  const loseLives = (n) => {
    setMistakes((m) => m + 1);
    const left = Math.max(0, lives - n);
    setLives(left);
    if (left === 0) later(endGame, 500);
    return left;
  };

  const handleClick = (i) => {
    if (phase !== "play" || statuses[i]) return;
    const value = layout[i];
    if (value === BOMB) {
      setStatuses((s) => ({ ...s, [i]: "bomb" }));
      play("bomb");
      loseLives(2);
    } else if (value === next) {
      setStatuses((s) => ({ ...s, [i]: "correct" }));
      if (next === mode.numbers) {
        play("win");
        const stats = scoreRound(mode, (Date.now() - playStart.current) / 1000, lives, timerOn, mistakes === 0);
        setRoundStats(stats);
        setPoints((p) => p + stats.total);
        setScore((s) => s + 1);
        setPhase("roundWin");
        later(() => startRound(round + 1, layout), 1500);
      } else {
        play("correct");
        setNext(next + 1);
      }
    } else {
      setStatuses((s) => ({ ...s, [i]: "wrong" }));
      play("wrong");
      if (loseLives(1) > 0) later(() => setStatuses((s) => { const c = { ...s }; delete c[i]; return c; }), 700);
    }
  };

  const restart = () => {
    timers.current.forEach(clearTimeout);
    setScore(0);
    setPoints(0);
    setLives(mode.lives);
    setNewBest(false);
    setBest(getBest(mode.id, timerOn));
    celebrated.current = false;
    startRound(1);
  };

  const pendingOver = lives === 0 && phase === "play";

  return (
    <div className="mx-auto min-h-screen max-w-4xl px-4 py-6 sm:px-8 sm:py-10">
      <Hud mode={mode} lives={lives} points={points} best={best} round={round} timerOn={timerOn} muted={muted} onMute={onMute} onExit={onExit} />
      <StatusBar phase={phase} timerOn={timerOn} countdown={countdown} total={mode.memorize} next={next} elapsed={elapsed} onReady={beginPlay} category={category} />
      {category.id !== "numbers" && phase !== "over" && <SequenceStrip category={category} count={mode.numbers} next={phase === "memorize" ? 0 : next} />}
      <AnimatePresence>
        {bestFlash && (
          <motion.div data-testid="new-best-flash" initial={{ opacity: 0, y: -10, scale: 0.8 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -10 }}
            className="fixed left-1/2 top-4 z-40 -translate-x-1/2 rounded-full bg-gradient-to-r from-amber-400 to-fuchsia-500 px-5 py-2 font-display font-black text-white shadow-2xl">
            <Sparkles className="mr-1 inline h-4 w-4" /> New best score!
          </motion.div>
        )}
      </AnimatePresence>
      <div className="relative">
        <div data-testid="tile-grid" data-phase={phase} data-category={category.id} className="tile-grid mx-auto mt-2 grid gap-2 sm:gap-3" style={{ "--cols": mode.cols, maxWidth: mode.cols * 130 }}>
          {layout.map((v, i) => (
            <Tile key={`${round}-${i}`} index={i} value={v} status={statuses[i]} faceUp={phase === "memorize" || phase === "over" || !!statuses[i]} onClick={handleClick} category={category} disabled={phase !== "play" || pendingOver} />
          ))}
        </div>
        <AnimatePresence>
          {phase === "roundWin" && roundStats && <RoundBanner round={round} stats={roundStats} />}
        </AnimatePresence>
      </div>
      <GameOverDialog open={phase === "over"} score={score} points={points} best={best} isNewBest={newBest} modeName={mode.name} timerOn={timerOn} onPlayAgain={restart} onMenu={onExit} />
    </div>
  );
}
